import { useState, useEffect } from "react";
import { Wind, AlertCircle, Leaf, Activity, Waves } from "lucide-react";
import UrgeSurf from "./UrgeSurf";

const TABS = [
  { id: "urgesurf",    label: "Impulso",     icon: Waves,       testid: "tab-urgesurf" },
  { id: "breathing",   label: "Respiración", icon: Wind,        testid: "tab-breathing" },
  { id: "halt",        label: "HALT",        icon: AlertCircle, testid: "tab-halt" },
  { id: "mindfulness", label: "Grounding",   icon: Leaf,        testid: "tab-mindfulness" },
  { id: "thermometer", label: "Termómetro",  icon: Activity,    testid: "tab-thermometer" },
];

// ===== BREATHING COMPONENT =====
function Breathing() {
  const PHASES = [
    { label: "Inhala", duration: 4, targetScale: 1.5, color: "#6B9080", bg: "#EEF4F1" },
    { label: "Mantén", duration: 7, targetScale: 1.5, color: "#5B8FCC", bg: "#EDF4FF" },
    { label: "Exhala", duration: 8, targetScale: 1, color: "#84A98C", bg: "#EEF6EE" },
  ];
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(4);
  const [cycles, setCycles] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [circleScale, setCircleScale] = useState(1);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isActive) return;
    if (count > 0) {
      const t = setTimeout(() => setCount(c => c - 1), 1000);
      return () => clearTimeout(t);
    } else {
      const nextPhase = (phase + 1) % PHASES.length;
      const newCycles = nextPhase === 0 ? cycles + 1 : cycles;
      if (nextPhase === 0 && newCycles >= 4) {
        setIsActive(false);
        setDone(true);
        return;
      }
      if (nextPhase === 0) setCycles(newCycles);
      setPhase(nextPhase);
      setCircleScale(PHASES[nextPhase].targetScale);
      setCount(PHASES[nextPhase].duration);
    }
  }, [isActive, count, phase, cycles]);

  const start = () => {
    setIsActive(true);
    setDone(false);
    setPhase(0);
    setCycles(0);
    setCount(4);
    setCircleScale(1.5);
  };

  const stop = () => {
    setIsActive(false);
    setCircleScale(1);
    setPhase(0);
    setCount(4);
  };

  const currentPhase = PHASES[phase];

  return (
    <div className="py-4">
      <div className="mb-4 rounded-2xl overflow-hidden h-28">
        <img src="https://images.unsplash.com/photo-1468581264429-2548ef9eb732?crop=entropy&cs=srgb&fm=jpg&q=60&w=600" alt="Calma" className="w-full h-full object-cover" />
      </div>
      <p className="text-sm text-[#2C4238] mb-6 leading-relaxed text-center">
        La respiración 4-7-8 activa el nervio vago y reduce el cortisol en menos de 2 minutos.
      </p>
      <div className="flex flex-col items-center">
        <div className="relative w-44 h-44 flex items-center justify-center mb-6">
          <div
            className="absolute rounded-full opacity-30"
            style={{
              width: "11rem", height: "11rem",
              backgroundColor: currentPhase.color,
              transform: `scale(${circleScale * 1.15})`,
              transition: `transform ${currentPhase.duration}s ease-in-out`,
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: "10rem", height: "10rem",
              backgroundColor: currentPhase.color,
              transform: `scale(${circleScale})`,
              transition: `transform ${currentPhase.duration}s ease-in-out, background-color 0.5s ease`,
            }}
          />
          <div className="relative text-center text-white z-10">
            {isActive ? (
              <>
                <p className="text-4xl font-bold" style={{ fontFamily: "Manrope" }}>{count}</p>
                <p className="text-sm font-medium">{currentPhase.label}</p>
              </>
            ) : done ? (
              <p className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Excelente</p>
            ) : (
              <p className="text-sm font-semibold">Toca para<br/>comenzar</p>
            )}
          </div>
        </div>

        <div className="flex gap-3 items-center mb-4">
          {PHASES.map((p, i) => (
            <div key={i} className="text-center">
              <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${phase === i && isActive ? "" : "opacity-30"}`} style={{ backgroundColor: p.color }} />
              <p className="text-xs text-[#2C4238]">{p.label} {p.duration}s</p>
            </div>
          ))}
        </div>

        {isActive ? (
          <button data-testid="stop-breathing" onClick={stop} className="px-8 py-3 rounded-full bg-[#E4EBE6] text-[#2C4238] font-semibold text-sm">
            Detener
          </button>
        ) : (
          <button data-testid="start-breathing" onClick={start} className="px-8 py-3 rounded-full bg-[#6B9080] text-white font-semibold text-sm">
            {done ? "Repetir" : "Comenzar (4 ciclos)"}
          </button>
        )}
      </div>
    </div>
  );
}

// ===== HALT COMPONENT =====
const HALT_ITEMS = [
  { key: "H", question: "¿Tienes Hambre?", detail: "El azúcar baja activa el cerebro de recompensa y aumenta la impulsividad.", color: "#F4A261" },
  { key: "A", question: "¿Estás Enojado/a?", detail: "La ira activa la amígdala y reduce el control prefrontal — combinación peligrosa.", color: "#E07A5F" },
  { key: "L", question: "¿Te sientes Solo/a?", detail: "La soledad aumenta el cortisol y activa el ansia de conexión/sustancia.", color: "#8B69CC" },
  { key: "T", question: "¿Estás Cansado/a?", detail: "El cansancio debilita la corteza prefrontal, reduciendo el autocontrol.", color: "#5B8FCC" },
];

function Halt() {
  const [answers, setAnswers] = useState({ H: null, A: null, L: null, T: null });
  const [submitted, setSubmitted] = useState(false);

  const toggle = (key, val) => setAnswers(a => ({ ...a, [key]: val }));
  const alerts = HALT_ITEMS.filter(i => answers[i.key] === true);
  const allAnswered = Object.values(answers).every(v => v !== null);

  return (
    <div className="py-4">
      <p className="text-sm text-[#2C4238] mb-4 leading-relaxed">
        Antes de actuar, evalúa tu estado. HALT identifica estados de vulnerabilidad neurológica.
      </p>
      <div className="space-y-3">
        {HALT_ITEMS.map(({ key, question, detail, color }) => (
          <div key={key} className="bg-white rounded-2xl p-4 border border-[#E4EBE6]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: color }}>
                {key}
              </div>
              <p className="font-semibold text-sm text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>{question}</p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid={`halt-${key}-yes`}
                onClick={() => toggle(key, true)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${answers[key] === true ? "text-white border-transparent" : "bg-white text-[#2C4238] border-[#E4EBE6]"}`}
                style={answers[key] === true ? { backgroundColor: color, borderColor: color } : {}}
              >
                Sí
              </button>
              <button
                data-testid={`halt-${key}-no`}
                onClick={() => toggle(key, false)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${answers[key] === false ? "bg-[#EEF4F1] text-[#6B9080] border-[#6B9080]" : "bg-white text-[#2C4238] border-[#E4EBE6]"}`}
              >
                No
              </button>
            </div>
          </div>
        ))}
      </div>
      {allAnswered && alerts.length > 0 && (
        <div className="mt-4 p-4 bg-[#FFF9EE] rounded-2xl border border-[#F4C5B5]">
          <p className="text-sm font-semibold text-[#1E2F23] mb-2" style={{ fontFamily: "Manrope" }}>Atención</p>
          {alerts.map(({ key, question, detail, color }) => (
            <p key={key} className="text-xs text-[#2C4238] mb-1 leading-relaxed">
              <span className="font-semibold" style={{ color }}>• {question.replace("¿", "").replace("?", "")}</span>: {detail}
            </p>
          ))}
          <p className="text-sm text-[#6B9080] font-medium mt-2">Atiende estas necesidades antes de exponerte a situaciones de riesgo.</p>
        </div>
      )}
      {allAnswered && alerts.length === 0 && (
        <div className="mt-4 p-4 bg-[#EEF4F1] rounded-2xl">
          <p className="text-sm text-[#6B9080] font-medium">Tu estado actual es estable. Sigue con tu plan de recuperación.</p>
        </div>
      )}
    </div>
  );
}

// ===== GROUNDING 5-4-3-2-1 =====
const GROUNDING_STEPS = [
  { n: 5, sense: "VE", detail: "Nombra 5 cosas que puedes VER ahora mismo", icon: "👁", color: "#6B9080" },
  { n: 4, sense: "TOCA", detail: "Nombra 4 cosas que puedes TOCAR o sentir físicamente", icon: "✋", color: "#5B8FCC" },
  { n: 3, sense: "ESCUCHA", detail: "Nombra 3 sonidos que puedes ESCUCHAR", icon: "👂", color: "#8B69CC" },
  { n: 2, sense: "HUELE", detail: "Nombra 2 cosas que puedes OLER (o recordar olores)", icon: "👃", color: "#C47840" },
  { n: 1, sense: "SABOREA", detail: "Nombra 1 cosa que puedes SABOREAR", icon: "👅", color: "#E07A5F" },
];

function Grounding() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const current = GROUNDING_STEPS[step];

  return (
    <div className="py-4">
      <div className="mb-4 rounded-2xl overflow-hidden h-28">
        <img src="https://images.unsplash.com/photo-1505635725851-c2cfe9e29112?crop=entropy&cs=srgb&fm=jpg&q=60&w=600" alt="Mindfulness" className="w-full h-full object-cover" />
      </div>
      <p className="text-sm text-[#2C4238] mb-4 leading-relaxed">
        El 5-4-3-2-1 activa los sentidos físicos, interrumpiendo el circuito de rumia y regresando tu atención al presente.
      </p>
      {!done ? (
        <div className="bg-white rounded-2xl p-6 border border-[#E4EBE6] text-center">
          <span className="text-4xl mb-3 block">{current.icon}</span>
          <p className="text-4xl font-bold mb-2" style={{ color: current.color, fontFamily: "Manrope" }}>{current.n}</p>
          <p className="text-sm font-semibold text-[#1E2F23] mb-2" style={{ fontFamily: "Manrope" }}>{current.sense}</p>
          <p className="text-sm text-[#2C4238] mb-6 leading-relaxed">{current.detail}</p>
          <div className="flex gap-1 justify-center mb-4">
            {GROUNDING_STEPS.map((s, i) => (
              <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: i <= step ? current.color : "#E4EBE6" }} />
            ))}
          </div>
          <button
            data-testid={`grounding-step-${step}`}
            onClick={() => step < GROUNDING_STEPS.length - 1 ? setStep(s => s + 1) : setDone(true)}
            className="px-8 py-3 rounded-full text-white font-semibold text-sm"
            style={{ backgroundColor: current.color }}
          >
            {step < GROUNDING_STEPS.length - 1 ? "Siguiente" : "Completar"}
          </button>
        </div>
      ) : (
        <div className="bg-[#EEF4F1] rounded-2xl p-6 text-center">
          <p className="text-3xl mb-2">✓</p>
          <p className="font-bold text-[#1E2F23] mb-2" style={{ fontFamily: "Manrope" }}>Ejercicio completado</p>
          <p className="text-sm text-[#2C4238] mb-4">Tu sistema nervioso está más calmado. El hipocampo acaba de crear un nuevo recuerdo de calma.</p>
          <button data-testid="grounding-restart" onClick={() => { setStep(0); setDone(false); }} className="px-6 py-2.5 rounded-full bg-[#6B9080] text-white text-sm font-medium">
            Repetir
          </button>
        </div>
      )}
    </div>
  );
}

// ===== RISK THERMOMETER =====
function Thermometer() {
  const [level, setLevel] = useState(5);
  const [saved, setSaved] = useState(false);

  const getColor = (v) => v <= 3 ? "#84A98C" : v <= 6 ? "#F4A261" : "#E07A5F";
  const getLabel = (v) => v <= 2 ? "Seguro" : v <= 4 ? "Estable" : v <= 6 ? "Atención" : v <= 8 ? "Alto riesgo" : "Crisis";
  const getMessage = (v) => {
    if (v <= 2) return "Tu corteza prefrontal está funcionando bien. Es un buen momento para practicar nuevas conductas sanas.";
    if (v <= 4) return "Estado manejable. Practica HALT, mantén tu rutina de ejercicio y contacta a tu red si sientes que sube.";
    if (v <= 6) return "Nivel de atención. Activa tu Plan B, avisa a un contacto de tu red y considera aplicar el Plan PAUSE preventivamente.";
    if (v <= 8) return "Riesgo alto. Aplica el Plan PAUSE ahora, llama a alguien de tu red, evita situaciones de riesgo y considera contactar a tu terapeuta hoy.";
    return "Nivel de crisis. Activa el Plan PAUSE inmediatamente, llama a tu contacto de emergencia o a la línea de crisis.";
  };

  const saveEntry = () => {
    const entries = JSON.parse(localStorage.getItem("reconectados_risk_history") || "[]");
    entries.push({ date: new Date().toISOString(), level });
    localStorage.setItem("reconectados_risk_history", JSON.stringify(entries));
    const journal = JSON.parse(localStorage.getItem("reconectados_journal") || "[]");
    if (journal.length > 0) {
      journal[journal.length - 1].riskLevel = level;
      localStorage.setItem("reconectados_journal", JSON.stringify(journal));
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const color = getColor(level);

  return (
    <div className="py-4">
      <p className="text-sm text-[#2C4238] mb-6 leading-relaxed">
        Evalúa tu nivel de riesgo de recaída hoy. La conciencia temprana es la herramienta más poderosa de prevención.
      </p>
      <div className="bg-white rounded-2xl p-6 border border-[#E4EBE6]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[#2C4238]">Nivel de riesgo</span>
          <span className="text-2xl font-bold" style={{ color, fontFamily: "Manrope" }}>{level}</span>
        </div>
        <div className="h-4 rounded-full mb-2 risk-gradient" />
        <input
          data-testid="risk-slider"
          type="range"
          min="1"
          max="10"
          value={level}
          onChange={e => setLevel(Number(e.target.value))}
          className="w-full mt-2 accent-[#6B9080]"
        />
        <div className="flex justify-between text-xs text-[#2C4238] mt-1">
          <span>1 Bajo</span>
          <span>5 Medio</span>
          <span>10 Crisis</span>
        </div>
        <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: `${color}20` }}>
          <p className="text-sm font-semibold mb-1" style={{ color, fontFamily: "Manrope" }}>{getLabel(level)}</p>
          <p className="text-xs text-[#1E2F23] leading-relaxed">{getMessage(level)}</p>
        </div>
        <button
          data-testid="save-risk"
          onClick={saveEntry}
          className="w-full mt-4 py-3 rounded-2xl text-white font-semibold text-sm transition-all"
          style={{ backgroundColor: saved ? "#84A98C" : color }}
        >
          {saved ? "Guardado" : "Registrar nivel del día"}
        </button>
      </div>
    </div>
  );
}

// ===== MAIN TOOLS PAGE =====
export default function Tools() {
  const [activeTab, setActiveTab] = useState("breathing");

  return (
    <div className="page-container">
      <div className="animate-fade-in-up mb-5">
        <h1 className="text-3xl font-bold text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>Herramientas</h1>
        <p className="text-sm text-[#2C4238] mt-1">Técnicas basadas en neurociencia</p>
      </div>

      {/* Tabs */}
      <div className="animate-fade-in-up delay-100 flex gap-2 overflow-x-auto pb-1 mb-2">
        {TABS.map(({ id, label, icon: Icon, testid }) => (
          <button
            key={id}
            data-testid={testid}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 border flex-shrink-0 ${
              activeTab === id
                ? "bg-[#6B9080] text-white border-[#6B9080]"
                : "bg-white text-[#2C4238] border-[#E4EBE6]"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {activeTab === "urgesurf"    && <UrgeSurf />}
        {activeTab === "breathing"   && <Breathing />}
        {activeTab === "halt"        && <Halt />}
        {activeTab === "mindfulness" && <Grounding />}
        {activeTab === "thermometer" && <Thermometer />}
      </div>
    </div>
  );
}
